import { useCallback, useState, useEffect, useMemo } from 'react';
import { useAtomValue } from 'jotai';
import {
  Stack,
  Typography,
  Button,
  IconButton,
  Tooltip,
  TextField,
  Checkbox,
  FormControlLabel,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import Edit from '@mui/icons-material/Edit';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import Delete from '@mui/icons-material/Delete';
import ArrowUpward from '@mui/icons-material/ArrowUpward';
import ArrowDownward from '@mui/icons-material/ArrowDownward';
import { containedButtonStyle } from '@/theme';
import { abyssColors } from '@/theme/colors';
import { FullWidthPanel } from './styles';
import { GenericDialog } from '../../shared/GenericDialog';
import { assemblyTemplate, selectedTemplateId, allPOITypes } from '../state';
import { removeTypename, useSnackBarMessage } from '../utils';
import {
  AllTemplatesForBuilderDocument,
  GetVersionsDocument,
  ReviewActionChecklistConfig,
  TemplateTypeEnum,
  useUpdateTemplateMutation,
} from '@/__generated__/graphql';

export const ReviewActionChecklistConfigForm = ({ structureId }: { structureId: string }) => {
  const templateId = useAtomValue(selectedTemplateId);
  const selectedAssemblyTemplate = useAtomValue(assemblyTemplate);
  const allPointOfInterestTemplates = useAtomValue(allPOITypes);
  const [reviewActionChecklistConfig, setReviewActionChecklistConfig] = useState<
    ReviewActionChecklistConfig[]
  >([]);
  const [mode, setMode] = useState<'ReadOnly' | 'Edit'>('ReadOnly');
  const [originalConfig, setOriginalConfig] = useState<ReviewActionChecklistConfig[]>([]);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);

  const reviewActionOptions = useMemo(() => {
    const fromTemplates: string[] = [];
    if (allPointOfInterestTemplates) {
      for (const template of allPointOfInterestTemplates) {
        const actionField = template.fields?.find((field) => field.key === 'review_action');
        if (actionField) {
          if (actionField.options?.length) {
            fromTemplates.push(...actionField.options);
          }
          const fromOptionsData = actionField.optionsData?.map((d) => d.value).filter(Boolean);
          if (fromOptionsData?.length) {
            fromTemplates.push(...fromOptionsData);
          }
        }
      }
    }
    const existingValues = (reviewActionChecklistConfig ?? [])
      .map((item) => item.reviewActionValue?.trim())
      .filter(Boolean);
    return [...new Set([...fromTemplates, ...existingValues])];
  }, [allPointOfInterestTemplates, reviewActionChecklistConfig]);

  useEffect(() => {
    const config = removeTypename(selectedAssemblyTemplate?.reviewActionChecklistConfig) ?? [];
    if (mode !== 'Edit') {
      setReviewActionChecklistConfig(config);
      setOriginalConfig(config);
    }
  }, [selectedAssemblyTemplate, mode]);

  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });

  const handleEdit = useCallback(() => {
    setOriginalConfig(JSON.parse(JSON.stringify(reviewActionChecklistConfig)));
    setMode('Edit');
  }, [reviewActionChecklistConfig]);

  const handleCancel = useCallback(() => {
    setReviewActionChecklistConfig(originalConfig);
    setMode('ReadOnly');
  }, [originalConfig]);

  const updateConfigItem = useCallback(
    (index: number, field: keyof ReviewActionChecklistConfig, value: string | boolean | number) => {
      setReviewActionChecklistConfig((prev) => {
        const next = [...prev];
        next[index] = { ...next[index], [field]: value };
        return next;
      });
    },
    []
  );

  const handleReviewActionValueChange = useCallback((index: number, newValue: string) => {
    setReviewActionChecklistConfig((prev) => {
      const next = [...prev];
      const current = next[index];
      next[index] = {
        ...current,
        reviewActionValue: newValue,
        displayName: newValue,
      };
      return next;
    });
  }, []);

  const addNewItem = useCallback(() => {
    const maxPosition =
      reviewActionChecklistConfig.length > 0
        ? Math.max(...reviewActionChecklistConfig.map((item) => item.position ?? 0))
        : -1;

    setReviewActionChecklistConfig((prev) => [
      ...prev,
      {
        id: '',
        reviewActionValue: '',
        displayName: '',
        isEnabled: true,
        position: maxPosition + 1,
      },
    ]);
  }, [reviewActionChecklistConfig]);

  const moveItem = useCallback((index: number, direction: 'up' | 'down') => {
    setReviewActionChecklistConfig((prev) => {
      const sorted = [...prev].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
      const newIndex = direction === 'up' ? index - 1 : index + 1;

      if (newIndex < 0 || newIndex >= sorted.length) return prev;

      const temp = sorted[index];
      sorted[index] = sorted[newIndex];
      sorted[newIndex] = temp;

      // Update all positions
      sorted.forEach((item, idx) => {
        item.position = idx;
      });

      return sorted;
    });
  }, []);

  const [updateReviewActionChecklistConfig] = useUpdateTemplateMutation({
    refetchQueries: [AllTemplatesForBuilderDocument, GetVersionsDocument],
    onCompleted: (response) => {
      setIsDeleteDialogOpen(false);
      setDeleteIndex(null);
      setMode('ReadOnly');
      if (!response?.updateTemplate) {
        showSnackBar(
          'An error occurred while updating review action checklist configuration',
          'error'
        );
        return;
      }
      showSnackBar('Review Action Checklist Configuration saved successfully', 'success');
    },
    onError: (error) => {
      console.error(error);
      setIsDeleteDialogOpen(false);
      setDeleteIndex(null);
      showSnackBar(error.message, 'error');
    },
  });

  const saveConfig = useCallback(async () => {
    if (!reviewActionChecklistConfig) return;

    const payload = reviewActionChecklistConfig.map((item, index) => ({
      id: item.id ?? '',
      reviewActionValue: item.reviewActionValue,
      displayName: item.displayName,
      isEnabled: item.isEnabled ?? true,
      position: item.position ?? index,
    }));

    await updateReviewActionChecklistConfig({
      variables: {
        input: {
          type: TemplateTypeEnum.Assembly,
          structureId,
          templateId: templateId ?? '',
          reviewActionChecklistConfig: payload,
          lastModifiedFor: 'Update Review Action Checklist Config',
        },
      },
    });
  }, [reviewActionChecklistConfig, structureId, templateId, updateReviewActionChecklistConfig]);

  const handleDeleteClick = useCallback((index: number) => {
    setIsDeleteDialogOpen(true);
    setDeleteIndex(index);
  }, []);

  const handleDeleteConfirm = useCallback(() => {
    if (deleteIndex !== null) {
      const sorted = [...reviewActionChecklistConfig].sort(
        (a, b) => (a.position ?? 0) - (b.position ?? 0)
      );
      const filtered = sorted.filter((_, i) => i !== deleteIndex);
      // Update positions after removal
      filtered.forEach((item, idx) => {
        item.position = idx;
      });
      setReviewActionChecklistConfig(filtered);
      setIsDeleteDialogOpen(false);
      setDeleteIndex(null);
      // Save immediately after deletion
      const payload = filtered.map((item, index) => ({
        id: item.id ?? '',
        reviewActionValue: item.reviewActionValue,
        displayName: item.displayName,
        isEnabled: item.isEnabled ?? true,
        position: item.position ?? index,
      }));
      updateReviewActionChecklistConfig({
        variables: {
          input: {
            type: TemplateTypeEnum.Assembly,
            structureId,
            templateId: templateId ?? '',
            reviewActionChecklistConfig: payload,
            lastModifiedFor: 'Update Review Action Checklist Config',
          },
        },
      });
    }
  }, [
    deleteIndex,
    reviewActionChecklistConfig,
    structureId,
    templateId,
    updateReviewActionChecklistConfig,
  ]);

  return (
    <>
      <Stack
        width="100%"
        marginTop="1.2rem"
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        height="4.8rem"
      >
        <Typography fontSize="2.2rem" fontWeight={500}>
          Review Action Checklist Configuration
        </Typography>
        {mode === 'Edit' ? (
          <Stack direction="row" spacing={1} sx={{ pb: 0.5 }}>
            <Button variant="contained" onClick={saveConfig} sx={containedButtonStyle}>
              Save
            </Button>
            <Button variant="contained" onClick={handleCancel} sx={containedButtonStyle}>
              Cancel
            </Button>
          </Stack>
        ) : (
          <Stack direction="row" spacing={0} sx={{ pb: 0.5 }}>
            <Tooltip title="Edit Review Action Checklist Configuration" placement="top" arrow>
              <IconButton size="medium" onClick={handleEdit}>
                <Edit
                  fontSize="inherit"
                  sx={{ cursor: 'pointer', color: abyssColors.primary[500] }}
                />
              </IconButton>
            </Tooltip>
          </Stack>
        )}
      </Stack>

      <FullWidthPanel>
        <Stack sx={{ flex: 1, gap: '2rem' }}>
          {reviewActionChecklistConfig?.length > 0 ? (
            <>
              {reviewActionChecklistConfig
                .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
                .map((item, index) => (
                  <Stack
                    key={`${item.id}-${index}`}
                    sx={{
                      p: 2,
                      gap: 1.5,
                      borderRadius: 1,
                      bgcolor: abyssColors.neutral[50],
                      border: `1px solid ${abyssColors.neutral[300]}`,
                    }}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      gap={2}
                    >
                      <Stack direction="row" alignItems="center" gap={1} sx={{ flex: 1 }}>
                        {mode === 'Edit' && (
                          <Stack direction="row" gap={0.5}>
                            <Tooltip title="Move up">
                              <IconButton
                                size="small"
                                onClick={() => moveItem(index, 'up')}
                                disabled={index === 0}
                              >
                                <ArrowUpward fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Move down">
                              <IconButton
                                size="small"
                                onClick={() => moveItem(index, 'down')}
                                disabled={index === reviewActionChecklistConfig.length - 1}
                              >
                                <ArrowDownward fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        )}

                        <Grid container spacing={2} sx={{ flex: 1 }}>
                          <Grid size={{ xs: 12, md: 4 }}>
                            {mode === 'Edit' ? (
                              <FormControl fullWidth required>
                                <InputLabel id={`review-action-value-label-${index}`}>
                                  Review Action Value
                                </InputLabel>
                                <Select
                                  labelId={`review-action-value-label-${index}`}
                                  id={`review-action-value-${index}`}
                                  value={item.reviewActionValue}
                                  label="Review Action Value"
                                  onChange={(e) =>
                                    handleReviewActionValueChange(index, e.target.value)
                                  }
                                >
                                  <MenuItem value="">
                                    <em>Select review action...</em>
                                  </MenuItem>
                                  {reviewActionOptions.map((option) => (
                                    <MenuItem key={option} value={option}>
                                      {option}
                                    </MenuItem>
                                  ))}
                                </Select>
                                {reviewActionOptions.length === 0 && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ mt: 0.5, display: 'block' }}
                                  >
                                    Add a Review Action field with options to a POI template linked
                                    to this assembly to see options here.
                                  </Typography>
                                )}
                              </FormControl>
                            ) : (
                              <Stack>
                                <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5 }}>
                                  Review Action Value:
                                </Typography>
                                <Typography variant="body1">{item.reviewActionValue}</Typography>
                              </Stack>
                            )}
                          </Grid>

                          <Grid size={{ xs: 12, md: 4 }}>
                            {mode === 'Edit' ? (
                              <TextField
                                label="Display Name"
                                value={item.displayName}
                                onChange={(e) =>
                                  updateConfigItem(index, 'displayName', e.target.value)
                                }
                                fullWidth
                                required
                              />
                            ) : (
                              <Stack>
                                <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5 }}>
                                  Display Name:
                                </Typography>
                                <Typography variant="body1">{item.displayName}</Typography>
                              </Stack>
                            )}
                          </Grid>

                          <Grid size={{ xs: 12, md: 3 }}>
                            {mode === 'Edit' ? (
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={item.isEnabled ?? true}
                                    onChange={(e) =>
                                      updateConfigItem(index, 'isEnabled', e.target.checked)
                                    }
                                  />
                                }
                                label="Enabled"
                              />
                            ) : (
                              <Stack>
                                <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5 }}>
                                  Enabled:
                                </Typography>
                                <Typography variant="body1">
                                  {item.isEnabled ? 'Yes' : 'No'}
                                </Typography>
                              </Stack>
                            )}
                          </Grid>

                          <Grid size={{ xs: 12, md: 1 }}>
                            {mode === 'Edit' && (
                              <Tooltip title="Delete this item" placement="top" arrow>
                                <IconButton
                                  size="medium"
                                  onClick={() => handleDeleteClick(index)}
                                  sx={{ color: abyssColors.error[700] }}
                                >
                                  <Delete fontSize="inherit" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Grid>
                        </Grid>
                      </Stack>
                    </Stack>
                  </Stack>
                ))}
              {mode === 'Edit' && (
                <Button
                  startIcon={<AddCircleIcon />}
                  variant="contained"
                  onClick={addNewItem}
                  sx={containedButtonStyle}
                >
                  Add New Review Action
                </Button>
              )}
            </>
          ) : (
            <>
              {mode === 'Edit' ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                    No review actions configured. Click the button below to add one.
                  </Typography>
                  <Button
                    startIcon={<AddCircleIcon />}
                    variant="contained"
                    onClick={addNewItem}
                    sx={containedButtonStyle}
                  >
                    Add New Review Action
                  </Button>
                </Box>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    No review actions configured.
                  </Typography>
                </Box>
              )}
            </>
          )}
        </Stack>
      </FullWidthPanel>

      {isDeleteDialogOpen && (
        <GenericDialog
          open={isDeleteDialogOpen}
          onClose={() => {
            setIsDeleteDialogOpen(false);
            setDeleteIndex(null);
          }}
          title="Delete Review Action"
          content={
            <Typography fontWeight={400} fontSize="14px" color={abyssColors.primary[500]}>
              Are you sure you want to delete this review action? This action cannot be undone.
            </Typography>
          }
          actions={
            <>
              <Button
                variant="text"
                onClick={() => {
                  setIsDeleteDialogOpen(false);
                  setDeleteIndex(null);
                }}
                sx={{ color: abyssColors.primary[500] }}
              >
                Cancel
              </Button>
              <Button variant="contained" onClick={handleDeleteConfirm} sx={containedButtonStyle}>
                Delete
              </Button>
            </>
          }
          actionsStyles={{ mr: 3, pb: 3 }}
        />
      )}
    </>
  );
};
